import { Button, Col, Modal, Row, Spin } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIdentity } from 'src/core/services/authentication';
import { LoadingOutlined } from '@ant-design/icons';
import { container } from 'src/inversify.config';
import { UserItemStore } from 'src/stores/users-store';

const ChangeOrganizationModal: FC<{
  userId: string;
  currentOrg: { id: string; displayName: string };
  onSelect: (orgId: string) => void;
  onCancel: () => void;
}> = ({ userId, onSelect, currentOrg, onCancel }) => {
  const { t } = useTranslation();
  const userStore = container.get(UserItemStore);
  const [loading, setLoading] = useState<boolean>(false);
  const [memberships, setMemberships] = useState<{ id: string; displayName: string }[]>([currentOrg]);
  const [selection, setSelection] = useState<string>(currentOrg.id);

  const loadMembership = async (id: string) => {
    setLoading(true);
    const result = await userStore.getUserPartnerOrganizationsMembership(id);
    setMemberships(result);
    setLoading(false);
  };

  useEffect(() => {
    if (userId != null) loadMembership(userId);
    else setMemberships([]);
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      className="figma-modal"
      title={<h3 className="figma-modal__title">{t('ChangeOrganization')}</h3>}
      open={true}
      onCancel={onCancel}
      maskClosable={!loading}
      footer={
        <Row gutter={[24, 0]}>
          <Col span={12}>
            <Button disabled={loading} onClick={onCancel} className="big-modal-button primary-outline-button">
              {t('Cancel')}
            </Button>
          </Col>
          <Col span={12}>
            <Button disabled={loading || selection === currentOrg.id} onClick={() => onSelect(selection)} className="big-modal-button" type="primary">
              {t('Change')}
            </Button>
          </Col>
        </Row>
      }
    >
      <Spin spinning={loading}>
        <div style={{ marginBottom: 40 }}>
          {memberships.map(({ id, displayName }) => (
            <div
              onClick={() => setSelection(id)}
              key={id}
              className={`selectable-div ${id === selection ? 'current-selection' : ''} ${id === currentOrg.id ? 'current-org' : ''}`}
            >
              {displayName}
            </div>
          ))}
        </div>
      </Spin>
    </Modal>
  );
};

const OrganizationBox: FC = ({ ...props }) => {
  const identity = useIdentity();
  const currentOrganization = { id: identity?.profile['tenant_id'], displayName: identity?.profile['tenant_name'] };
  const [showModal, setShowModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  if (identity == null) return null;

  return (
    <>
      {loading && <Spin className="global-spinner" indicator={<LoadingOutlined style={{ fontSize: 50 }} spin />} size="large"></Spin>}
      <div onClick={() => setShowModal(true)} className="organization-box">
        <p>{currentOrganization.displayName}</p>
      </div>
      {showModal && (
        <ChangeOrganizationModal
          {...props}
          userId={identity.profile['sub']}
          currentOrg={currentOrganization}
          onCancel={() => setShowModal(false)}
          onSelect={(orgId) => {
            setShowModal(false);
            if (orgId !== currentOrganization.id) {
              setLoading(true);
              identity.changeOrganization(orgId);
            }
          }}
        />
      )}
    </>
  );
};

export default OrganizationBox;
